import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface ChartCardProps {
  title: string;
  caption?: string;
  /** Rendered above the plot; omit for single-series charts, which need no legend. */
  legend?: ReactNode;
  /** The accessible fallback the palette's contrast WARN obliges us to ship. */
  table: ReactNode;
  children: ReactNode;
}

/**
 * The shell every chart on the Equipa dashboard sits in: title, optional legend,
 * the plot, and a collapsible table so identity is never carried by colour alone.
 */
export function ChartCard({ title, caption, legend, table, children }: ChartCardProps) {
  const { t } = useTranslation();
  const [showTable, setShowTable] = useState(false);
  const tableId = useId();

  return (
    <section className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-[13px] font-semibold leading-tight text-conduril-ink">{title}</h3>
          {caption && (
            <p className="mt-1 text-[11px] leading-snug text-conduril-muted">{caption}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          aria-expanded={showTable}
          aria-controls={tableId}
          className="shrink-0 rounded-lg border border-conduril-border px-2.5 py-1.5 text-[10px]
                     font-semibold uppercase tracking-wide text-conduril-muted transition hover:text-conduril"
        >
          {showTable ? t('graficos.esconderTabela') : t('graficos.verTabela')}
        </button>
      </div>

      {legend && <div className="mt-3">{legend}</div>}

      <div className="mt-4">{children}</div>

      {showTable && (
        <div id={tableId} className="mt-4 overflow-x-auto border-t border-conduril-border pt-3">
          {table}
        </div>
      )}
    </section>
  );
}

/** Shared legend row: a colour chip plus its label, never colour alone. */
export function Legend({ items }: { items: { label: string; color: string }[] }) {
  return (
    <ul className="flex flex-wrap gap-x-3 gap-y-1.5">
      {items.map((item) => (
        <li key={item.label} className="flex items-center gap-1.5 text-[11px] text-[#5c6373]">
          <span
            aria-hidden
            className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
            style={{ background: item.color }}
          />
          {item.label}
        </li>
      ))}
    </ul>
  );
}

/** The plain table body used inside every ChartCard's fallback. */
export function DataTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: (string | number)[][];
}) {
  return (
    <table className="w-full text-left text-[11px]">
      <thead>
        <tr className="text-conduril-muted">
          {headers.map((h, i) => (
            <th key={h} scope="col" className={`pb-1.5 font-semibold ${i > 0 ? 'text-right' : ''}`}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} className="border-t border-conduril-border">
            {row.map((cell, ci) => (
              <td
                key={ci}
                className={`py-1.5 ${ci > 0 ? 'num text-right text-conduril-ink' : 'text-[#5c6373]'}`}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
